import { parseURL } from 'ufo';

import { ReportComponentType } from '@/types';
import { resolveCorporateLogo } from '@/utilities';

import { MemoInternalSourceType, MemoSourceItem } from './types';

export const resourcePathPatterns: Array<{
    label: string;
    pattern: RegExp;
    type: MemoInternalSourceType;
}> = [
    { label: 'Filing', pattern: /\/filings(?:\/|$)/, type: 'filing' },
    { label: 'Folder', pattern: /^\/folders\/([^/]+)/, type: 'folder' },
    {
        label: 'Investment',
        pattern: /^\/investments\/([^/]+)/,
        type: 'investment',
    },
    {
        label: 'Memo',
        pattern: /^\/memos\/([^/]+)|^\/(?:s|shared)\/memos\/([^/]+)/,
        type: 'memo',
    },
    {
        label: 'Pipeline',
        pattern: /^\/pipelines\/([^/]+)/,
        type: 'pipeline',
    },
    {
        label: 'Dashboard',
        pattern: /^\/(?:relations\/)?dashboards\/([^/]+)|^\/(?:s|shared)\/dashboards\/([^/]+)/,
        type: 'dashboard',
    },
    {
        label: 'List',
        pattern: /^\/(?:relations\/)?lists\/([^/]+)/,
        type: 'list',
    },
];

const sourceLabelByType: Record<MemoInternalSourceType, string> = {
    dashboard: 'Dashboard',
    filing: 'Filing',
    folder: 'Folder',
    investment: 'Investment',
    list: 'List',
    memo: 'Memo',
    pipeline: 'Pipeline',
    'report-component': 'Report Component',
    'workspace-resource': 'Workspace Resource',
};

const normalizeUrl = (href?: string | null) => {
    if (!href) {
        return undefined;
    }

    try {
        return new URL(href, window.location.origin).toString();
    } catch {
        return undefined;
    }
};

const sameOrigin = (url: URL) => url.origin === window.location.origin;

export const titleFromUrl = (url: string) => {
    try {
        const parsed = parseURL(url);

        return parsed.host || url;
    } catch {
        return url;
    }
};

export const parseHtmlBody = (body?: string) => {
    if (!body || typeof DOMParser === 'undefined') {
        return undefined;
    }

    return new DOMParser().parseFromString(body, 'text/html').body;
};

const classifyInternalUrl = (url: URL) => {
    const configuration = resourcePathPatterns.find((candidate) => candidate.pattern.test(url.pathname));

    if (!configuration) {
        return undefined;
    }

    const pathMatch = url.pathname.match(configuration.pattern);

    return {
        label: configuration.label,
        resourceId: pathMatch?.slice(1).find(Boolean),
        type: configuration.type,
    };
};

const isElementInsideSourceAtom = (element: HTMLElement) => {
    const filingSource = element.closest('[data-filing-source="true"], .filing-source-button');
    const sourcesPanel = element.closest('[data-memo-sources-panel="true"]');
    const reportComponent = element.closest('[data-memo-report-component-id], [data-report-component-embed]');
    const unfurledLink = element.closest('[data-type="unfurled-link"], unfurled-link');

    return Boolean(filingSource || sourcesPanel || reportComponent || unfurledLink);
};

const textContent = (element?: Element | null) => element?.textContent?.replace(/\s+/g, ' ').trim();

const readAttr = (element: Element, names: string[]) => {
    for (const name of names) {
        const value = element.getAttribute(name);

        if (value) {
            return value;
        }
    }

    return undefined;
};

export const scanSourceRoot = (root?: HTMLElement | null): MemoSourceItem[] => {
    if (!root) {
        return [];
    }

    const sources: MemoSourceItem[] = [];
    const seen = new Set<string>();

    const pushSource = (source: MemoSourceItem) => {
        if (seen.has(source.key)) {
            return;
        }

        seen.add(source.key);
        sources.push(source);
    };

    root.querySelectorAll<HTMLElement>('[data-filing-source="true"], .filing-source-button').forEach((element) => {
        const normalizedUrl = normalizeUrl(readAttr(element, ['data-source-url', 'href']));

        if (!normalizedUrl) {
            return;
        }

        const url = new URL(normalizedUrl);

        if (!sameOrigin(url)) {
            return;
        }

        const company =
            readAttr(element, ['data-company']) || textContent(element.querySelector('.filing-source-company'));
        const filing =
            readAttr(element, ['data-filing']) || textContent(element.querySelector('.filing-source-filing'));
        const website = readAttr(element, ['data-website']);
        const legacyLogoUrl = readAttr(element, ['data-logo-url']);
        const title = [company, filing].filter(Boolean).join(' › ') || 'Filing';
        const keyUrl = new URL(normalizedUrl);
        keyUrl.searchParams.delete('filingText');

        pushSource({
            description: 'Filing',
            image: resolveCorporateLogo(website, legacyLogoUrl),
            key: `internal:filing:${keyUrl.toString()}`,
            kind: 'internal',
            label: sourceLabelByType.filing,
            title,
            type: 'filing',
            url: normalizedUrl,
        });
    });

    root.querySelectorAll<HTMLElement>('[data-memo-report-component-id], [data-report-component-embed]').forEach(
        (element) => {
            const containingReportComponent = element.parentElement?.closest<HTMLElement>(
                '[data-memo-report-component-id], [data-report-component-embed]',
            );

            if (containingReportComponent) {
                return;
            }

            const reportComponentId =
                element.getAttribute('data-memo-report-component-id') ??
                element.getAttribute('data-report-component-id') ??
                undefined;

            if (!reportComponentId) {
                return;
            }

            const reportComponentType = element.getAttribute('data-memo-report-component-type') as
                ReportComponentType | undefined;
            const title =
                element.getAttribute('data-memo-toc-title') ||
                textContent(element.querySelector('[data-memo-toc-title]')) ||
                textContent(element) ||
                'Embedded Report Component';

            pushSource({
                key: `internal:report-component:${reportComponentId}`,
                kind: 'internal',
                label: sourceLabelByType['report-component'],
                reportComponentId,
                reportComponentType,
                title,
                type: 'report-component',
            });
        },
    );

    root.querySelectorAll<HTMLElement>('[data-type="unfurled-link"], unfurled-link').forEach((element) => {
        const normalizedUrl = normalizeUrl(readAttr(element, ['data-url', 'url']));

        if (!normalizedUrl) {
            return;
        }

        const url = new URL(normalizedUrl);
        const title = readAttr(element, ['data-title', 'title']) || titleFromUrl(normalizedUrl);
        const description = readAttr(element, ['data-description', 'description']);
        const image = readAttr(element, ['data-image', 'image']);
        const logo = readAttr(element, ['data-logo', 'logo']);
        const publisher = readAttr(element, ['data-publisher', 'publisher']);
        const date = readAttr(element, ['data-date', 'date']);
        const author = readAttr(element, ['data-author', 'author']);

        if (sameOrigin(url)) {
            const internal = classifyInternalUrl(url);

            if (internal) {
                pushSource({
                    description: internal.label,
                    key: `internal:url:${normalizedUrl}`,
                    kind: 'internal',
                    label: internal.label,
                    resourceId: internal.resourceId,
                    title,
                    type: internal.type,
                    url: normalizedUrl,
                });
            }

            return;
        }

        pushSource({
            author,
            date,
            description,
            image,
            key: `external:url:${normalizedUrl}`,
            kind: 'external',
            label: 'External Link',
            logo,
            publisher,
            title,
            url: normalizedUrl,
        });
    });

    root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((element) => {
        if (isElementInsideSourceAtom(element)) {
            return;
        }

        const normalizedUrl = normalizeUrl(element.getAttribute('href'));

        if (!normalizedUrl) {
            return;
        }

        const url = new URL(normalizedUrl);
        const title = textContent(element) || titleFromUrl(normalizedUrl);

        if (sameOrigin(url)) {
            const internal = classifyInternalUrl(url);

            if (!internal) {
                return;
            }

            pushSource({
                description: internal.label,
                key: `internal:url:${normalizedUrl}`,
                kind: 'internal',
                label: internal.label,
                resourceId: internal.resourceId,
                title,
                type: internal.type,
                url: normalizedUrl,
            });

            return;
        }

        pushSource({
            key: `external:url:${normalizedUrl}`,
            kind: 'external',
            label: 'External Link',
            title,
            url: normalizedUrl,
        });
    });

    return sources;
};

export const mergeSources = (primary: MemoSourceItem[], secondary: MemoSourceItem[]) => {
    const seen = new Set<string>();

    return [...primary, ...secondary].filter((source) => {
        if (seen.has(source.key)) {
            return false;
        }

        seen.add(source.key);
        return true;
    });
};

export const sourcesFromBody = (body?: string) => scanSourceRoot(parseHtmlBody(body));

export const sourceFingerprint = (sources: MemoSourceItem[]) =>
    sources
        .map((source) =>
            [
                source.key,
                source.kind,
                source.label,
                source.title,
                source.description,
                source.image,
                source.logo,
                source.publisher,
                source.author,
                source.date,
                source.url,
                source.reportComponentId,
                source.reportComponentType,
                source.resourceId,
                source.type,
            ].join(':'),
        )
        .join('|');
